/**
 * Helpers for building stable storage keys used while drilling down through
 * cascaded PFM mindmaps and per-node reports.
 *
 * - `scopeKey` identifies a single PFM node within a drill path. It is passed
 *   as the second argument to `loadPfmNodeReport` / `savePfmNodeReport` and
 *   the matching pending-request helpers, replacing the previous bare nodeKey.
 *   Top-level usage where `parents` is empty produces just the leaf nodeKey,
 *   so previously-saved reports continue to load.
 *
 * - `cacheKey` identifies a child mindmap within a drill path. It is passed as
 *   the `sessionKey` argument to `loadPfmMindmap` / `savePfmMindmap` and the
 *   matching pending-request helpers, so each child mindmap is stored
 *   independently from the top-level mindmap and from sibling drill chains.
 *   Top-level usage where `parents` is empty falls back to the chat sessionKey
 *   so previously-saved top-level mindmaps continue to load.
 */
export type DrillAncestor = { nodeKey: string; title: string };

/** Remove consecutive duplicate node_key entries (e.g. Analytics › Analytics). */
export function collapseConsecutiveDrillPath(path: DrillAncestor[]): DrillAncestor[] {
  const out: DrillAncestor[] = [];
  for (const item of path || []) {
    const key = String(item?.nodeKey || "").trim();
    if (!key) continue;
    const prev = out.length ? String(out[out.length - 1]?.nodeKey || "").trim() : "";
    if (prev === key) continue;
    out.push({
      nodeKey: key,
      title: stripPfmDisplayTitle(String(item?.title || key.split("/").pop() || key)),
    });
  }
  return out;
}

/** Append a child key to a drill path without duplicating the tail segment. */
export function appendDrillPathKey(parentKeys: string[], childKey: string): string[] {
  const parent = (parentKeys || []).map((k) => String(k || "").trim()).filter(Boolean);
  const child = String(childKey || "").trim();
  if (!child) return parent;
  if (parent.length && parent[parent.length - 1] === child) return parent;
  return [...parent, child];
}

export function buildScopeKey(parents: DrillAncestor[], nodeKey: string): string {
  const safeKey = String(nodeKey || "").trim();
  if (!parents || parents.length === 0) return safeKey;
  const segments = parents
    .map((p) => String(p?.nodeKey || "").trim())
    .filter(Boolean);
  return segments.length > 0 ? `${segments.join(">")}>${safeKey}` : safeKey;
}

export function buildMindmapCacheKey(sessionKey: string, parents: DrillAncestor[]): string {
  const safeSession = String(sessionKey || "").trim();
  if (!parents || parents.length === 0) return safeSession;
  const segments = parents
    .map((p) => String(p?.nodeKey || "").trim())
    .filter(Boolean);
  if (segments.length === 0) return safeSession;
  return `${safeSession}::path::${segments.join(">")}`;
}

export function formatPathBreadcrumb(parents: DrillAncestor[], leafTitle: string): string {
  const titles = (parents || [])
    .map((p) => stripPfmDisplayTitle(String(p?.title || "")))
    .filter(Boolean);
  const leaf = stripPfmDisplayTitle(String(leafTitle || ""));
  if (leaf) titles.push(leaf);
  return titles.filter(Boolean).join(" > ");
}

/** Mindmap label: no outline numbers (``1.``), HTML breaks, or leading list emojis. */
export function stripPfmDisplayTitle(title: string): string {
  let raw = String(title || "")
    .replace(/<br\s*\/?>/gi, " ")
    .replace(/\s+/g, " ")
    .trim();
  let prev = "";
  while (prev !== raw) {
    prev = raw;
    raw = raw.replace(/^[\s\u{1F300}-\u{1FAFF}]+/u, "");
    raw = raw.replace(/^(?:\d+\.)+\s*/, "");
    raw = raw.replace(/^\d+\s+/, "");
  }
  return raw.trim();
}

/**
 * Collapse consecutive duplicate path segments (``hub/hub/module`` → ``hub/module``).
 * Do not use this on committed-tree keys sent to the gateway — use
 * ``resolveCommittedNodeKeyInTree`` so API calls keep canonical DB node_key values.
 */
export function collapseDuplicateHubInNodeKey(nodeKey: string): string {
  const parts = String(nodeKey || "")
    .split("/")
    .map((p) => p.trim())
    .filter(Boolean);
  if (parts.length < 2) return String(nodeKey || "").trim();
  const out: string[] = [];
  for (const part of parts) {
    if (out.length > 0 && out[out.length - 1] === part) continue;
    out.push(part);
  }
  return out.join("/");
}

/** Map breadcrumb / shortened keys to canonical keys from committed ``pfm.tree`` flat_nodes. */
export function resolveCommittedNodeKeyInTree(
  nodeKey: string,
  knownKeys: Iterable<string>,
): string {
  const raw = String(nodeKey || "").trim();
  if (!raw) return "";
  const keys = [...knownKeys].map((k) => String(k).trim()).filter(Boolean);
  if (keys.includes(raw)) return raw;
  const collapsed = collapseDuplicateHubInNodeKey(raw);
  if (keys.includes(collapsed)) return collapsed;
  const collapsedMatches = keys.filter(
    (stored) => collapseDuplicateHubInNodeKey(stored) === collapsed,
  );
  if (collapsedMatches.length === 1) return collapsedMatches[0]!;
  if (collapsedMatches.length > 1) {
    const lastLower = (collapsed.split("/").pop() || "").toLowerCase();
    const suffixMatches = collapsedMatches.filter(
      (stored) => (stored.split("/").pop() || "").toLowerCase() === lastLower,
    );
    if (suffixMatches.length === 1) return suffixMatches[0]!;
  }
  const lastLower = (raw.split("/").pop() || "").toLowerCase();
  if (lastLower) {
    const segMatches = keys.filter(
      (stored) => (stored.split("/").pop() || "").toLowerCase() === lastLower,
    );
    if (segMatches.length === 1) return segMatches[0]!;
  }
  return raw;
}

export function collectNullParentHubKeys(
  parentByKey: Map<string, string | null | undefined>,
): Set<string> {
  const hubs = new Set<string>();
  for (const [key, parent] of parentByKey.entries()) {
    if (!String(parent || "").trim()) {
      const hubKey = String(key || "").trim();
      if (hubKey) hubs.add(hubKey);
    }
  }
  return hubs;
}

const CENTER_HUB_TITLES = new Set(["hub", "internal hub", "pfm hub", "center hub"]);

function slugLastSegment(nodeKey: string): string {
  const key = String(nodeKey || "").trim();
  const slash = key.lastIndexOf("/");
  return (slash >= 0 ? key.slice(slash + 1) : key).toLowerCase();
}

function isLikelyCenterHubRow(node: {
  node_key?: string;
  title?: string;
}): boolean {
  const key = String(node?.node_key || "").trim();
  if (!key) return false;
  const title = String(node?.title || "").trim().toLowerCase();
  const slug = slugLastSegment(key);
  if (slug === "hub" || slug === "pfm-hub" || slug === "product-hub" || slug.endsWith("-hub")) {
    return true;
  }
  if (CENTER_HUB_TITLES.has(title)) return true;
  const lower = key.toLowerCase();
  if (lower === "hub" || lower === "pfm-hub" || lower === "product-hub") return true;
  return false;
}

/** Build center hub keys from parent/title maps (e.g. execution results). */
export function collectCenterHubKeysFromParentMap(
  parentByKey: Map<string, string | null | undefined>,
  titleByKey?: Map<string, string>,
): Set<string> {
  const flat: Array<{ node_key?: string; parent_node_key?: string | null; title?: string }> = [];
  for (const [key, parent] of parentByKey.entries()) {
    flat.push({
      node_key: key,
      parent_node_key: parent ?? null,
      title: titleByKey?.get(key),
    });
  }
  return collectCenterHubKeysFromFlat(flat);
}

/** Internal center hub row keys (hidden); not every null-parent module after DB normalize. */
export function collectCenterHubKeysFromFlat(
  flat: Array<{ node_key?: string; parent_node_key?: string | null; title?: string }>,
): Set<string> {
  const nullParent: Array<{ node_key?: string; parent_node_key?: string | null; title?: string }> = [];
  const childParentKeys = new Set<string>();
  for (const node of flat || []) {
    const key = String(node?.node_key || "").trim();
    if (!key) continue;
    const parent = String(node?.parent_node_key ?? "").trim();
    if (parent) childParentKeys.add(parent);
    else nullParent.push(node);
  }
  if (!nullParent.length) return new Set();

  if (nullParent.length === 1) {
    const only = nullParent[0];
    const onlyKey = String(only?.node_key || "").trim();
    if (!onlyKey) return new Set();
    if (childParentKeys.has(onlyKey)) return new Set([onlyKey]);
    if (isLikelyCenterHubRow(only)) return new Set([onlyKey]);
    return new Set();
  }

  const hubs = new Set<string>();
  for (const node of nullParent) {
    const key = String(node?.node_key || "").trim();
    if (!key || !childParentKeys.has(key)) continue;
    if (isLikelyCenterHubRow(node)) hubs.add(key);
  }
  return hubs;
}

/**
 * Operator top-level module: parent is null (legacy) or parent is a center hub key.
 * The hub row itself is never top-level in the UI.
 */
export function isOperatorTopLevelNode(
  node: { node_key?: string; parent_node_key?: string | null; title?: string },
  centerHubKeys: Set<string>,
): boolean {
  const key = String(node?.node_key || "").trim();
  if (!key || centerHubKeys.has(key)) return false;
  const parent = String(node?.parent_node_key ?? "").trim();
  if (!parent) return true;
  if (centerHubKeys.has(parent)) return true;
  return false;
}

/** Build breadcrumb ancestors by walking parent_node_key edges (preferred). */
export function buildDrillPathFromParents(
  nodeKey: string,
  titleByKey: Map<string, string>,
  parentByKey: Map<string, string | null | undefined>,
  fallbackTitle: string,
): DrillAncestor[] {
  const collapsed = collapseDuplicateHubInNodeKey(nodeKey);
  const chain: DrillAncestor[] = [];
  const seen = new Set<string>();
  let current = collapsed;
  while (current && !seen.has(current)) {
    seen.add(current);
    const rawTitle =
      titleByKey.get(current)
      || (current === collapsed ? fallbackTitle : "")
      || current.split("/").pop()
      || current;
    chain.unshift({
      nodeKey: current,
      title: stripPfmDisplayTitle(rawTitle),
    });
    const parent = String(parentByKey.get(current) ?? "").trim();
    if (!parent) break;
    current = collapseDuplicateHubInNodeKey(parent);
  }
  if (chain.length) return chain;

  const parts = collapsed.split("/").filter(Boolean);
  const keys: string[] = [];
  for (let i = 0; i < parts.length; i += 1) {
    keys.push(parts.slice(0, i + 1).join("/"));
  }
  return keys.map((key, idx) => ({
    nodeKey: key,
    title: stripPfmDisplayTitle(
      titleByKey.get(key)
        || (idx === keys.length - 1 ? fallbackTitle : key.split("/").pop() || key),
    ),
  }));
}

/**
 * Drop internal null-parent hub nodes from operator breadcrumbs.
 * After Top, only product modules and deeper levels are shown.
 */
export function filterInternalHubFromDrillPath(
  path: DrillAncestor[],
  hubKeys?: Set<string>,
): DrillAncestor[] {
  if (!path.length) return path;

  const hubs =
    hubKeys && hubKeys.size > 0 ? hubKeys : inferHubKeysFromDrillPath(path);
  if (!hubs.size) return path;

  const filtered = path.filter((item) => !hubs.has(String(item.nodeKey || "").trim()));
  return filtered.length ? filtered : path;
}

function inferHubKeysFromDrillPath(path: DrillAncestor[]): Set<string> {
  const hubs = new Set<string>();
  if (path.length < 2) return hubs;

  const firstKey = String(path[0]?.nodeKey || "").trim();
  const secondKey = String(path[1]?.nodeKey || "").trim();
  if (!firstKey || !secondKey.startsWith(`${firstKey}/`)) return hubs;

  hubs.add(firstKey);

  // Duplicate hub segment: hub/hub/module → hide both hub crumbs.
  const duplicateHubKey = `${firstKey}/${firstKey}`;
  if (secondKey === duplicateHubKey || secondKey.startsWith(`${duplicateHubKey}/`)) {
    hubs.add(secondKey);
  }
  return hubs;
}
